import { useState } from 'react';
import {
  Box,
  Stack,
  Button,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Autocomplete,
  CircularProgress,
  Typography,
  InputAdornment,
  Divider,
  Alert,
} from '@mui/material';
import GroupIcon from '@mui/icons-material/Group';
import PersonIcon from '@mui/icons-material/Person';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { enGB } from 'date-fns/locale';
import { addYears, format, isSameMonth, parseISO, startOfDay } from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import { unwrap } from '@billda/api';
import { listGangs } from '../../../gangs/api/gangsApi';
import { useUserSearch } from '../../../users/hooks/useUsers';
import { useSitesSummary } from '../../../sites/hooks/useSitesSummary';
import { api } from '../../../../shared/api/billdaApi';
import type { PlotListItem } from '@billda/api';
import type { SiteSummary } from '../../../sites/types/sitesTypes';
import { useCreateAdminDeduction } from '../../hooks/useCreateAdminDeduction';
import { useDeductionTargetPeriod } from '../../hooks/useDeductionTargetPeriod';
import { useDebounce } from '../../../../shared/hooks/useDebounce';

type DeductionTarget = 'gang' | 'worker';

/** Dropdowns show the full first page rather than a token handful. */
const OPTION_PAGE_SIZE = 100;

const NOTES_MAX_LENGTH = 1000;

interface CreateDeductionPanelContentProps {
  onSuccess: () => void;
  onCancel: () => void;
}

/** "21–27 Jul" when the week sits in one month, "28 Jul – 3 Aug" when it straddles two. */
function formatWeekRange(start: string, end: string): string {
  const from = parseISO(start);
  const to = parseISO(end);

  return isSameMonth(from, to)
    ? `${format(from, 'd')}–${format(to, 'd MMM')}`
    : `${format(from, 'd MMM')} – ${format(to, 'd MMM')}`;
}

/** Why a pot won't take another deduction, in the words the office would use. */
function describeLockedPot(potStatus: string | null | undefined): string {
  switch (potStatus) {
    case 'Submitted':
    case 'Submitting':
      return 'has already been submitted';
    case 'Approved':
      return 'has already been approved';
    case 'Paid':
      return 'has already been paid';
    default:
      return 'is closed';
  }
}

function SectionHeading({ title, hint }: { title: string; hint?: string }) {
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      {hint && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
          {hint}
        </Typography>
      )}
    </Box>
  );
}

export function CreateDeductionPanelContent({
  onSuccess,
  onCancel,
}: CreateDeductionPanelContentProps) {
  const today = startOfDay(new Date());

  const [target, setTarget] = useState<DeductionTarget>('gang');
  const [selectedGangId, setSelectedGangId] = useState<string | null>(null);
  const [selectedWorkerId, setSelectedWorkerId] = useState<string | null>(null);
  const [selectedSite, setSelectedSite] = useState<SiteSummary | null>(null);
  const [selectedPlot, setSelectedPlot] = useState<PlotListItem | null>(null);
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [deductionDate, setDeductionDate] = useState<Date | null>(today);
  const [notes, setNotes] = useState('');

  // Search input state
  const [gangSearch, setGangSearch] = useState('');
  const [workerSearch, setWorkerSearch] = useState('');
  const [siteSearch, setSiteSearch] = useState('');
  const [plotSearch, setPlotSearch] = useState('');

  // Debounced search values
  const debouncedGangSearch = useDebounce(gangSearch, 300);
  const debouncedWorkerSearch = useDebounce(workerSearch, 300);
  const debouncedSiteSearch = useDebounce(siteSearch, 300);
  const debouncedPlotSearch = useDebounce(plotSearch, 300);

  // Gang query — server-side search
  const { data: gangsData, isFetching: gangsLoading } = useQuery({
    queryKey: ['gangs', 'search', debouncedGangSearch],
    queryFn: () => listGangs(debouncedGangSearch).then(r => r.gangs ?? []),
    staleTime: 60_000,
  });
  const gangs = gangsData ?? [];

  // Worker query — reuse existing hook (only enabled when >= 3 chars)
  const { data: userResults, isFetching: usersLoading } = useUserSearch(
    debouncedWorkerSearch,
    10
  );
  const users = userResults?.users ?? [];

  // Site query — server-side search
  const { data: sites = [], isFetching: sitesLoading } =
    useSitesSummary(debouncedSiteSearch);

  // Plot query — server-side search, keyed on site + search term
  const { data: plots = [], isFetching: plotsLoading } = useQuery({
    queryKey: [
      'plots',
      'deduction-panel',
      selectedSite?.id,
      debouncedPlotSearch,
    ],
    queryFn: async ({ signal }) => {
      const result = unwrap(
        await api.legacy.GET('/api/v2/sites/{siteId}/plots', {
          params: {
            path: { siteId: selectedSite!.id },
            query: {
              offset: 0,
              take: OPTION_PAGE_SIZE,
              search: debouncedPlotSearch || undefined,
            },
          },
          signal,
        })
      );
      return result.items;
    },
    enabled: !!selectedSite,
  });

  const { mutate: createDeduction, isPending } = useCreateAdminDeduction();

  const handleTargetChange = (
    _: React.MouseEvent<HTMLElement>,
    newTarget: DeductionTarget | null
  ) => {
    if (!newTarget) return;
    setTarget(newTarget);
    setSelectedGangId(null);
    setSelectedWorkerId(null);
    setGangSearch('');
    setWorkerSearch('');
  };

  const handleSiteChange = (newSite: SiteSummary | null) => {
    setSelectedSite(newSite);
    setSelectedPlot(null);
    setPlotSearch('');
  };

  const parsedAmount = parseFloat(amount);
  const amountNum = isNaN(parsedAmount) ? 0 : parsedAmount;
  const isDateInPast = !!deductionDate && deductionDate < today;

  // Ask the backend which week this date lands in, and whether that pot will take it
  const { data: targetPeriod, isFetching: targetPeriodLoading } =
    useDeductionTargetPeriod({
      date:
        deductionDate && !isDateInPast
          ? format(deductionDate, 'yyyy-MM-dd')
          : null,
      gangId: target === 'gang' ? selectedGangId : null,
      targetWorkerId: target === 'worker' ? selectedWorkerId : null,
    });

  const potIsLocked = targetPeriod?.canAddDeduction === false;

  const dateHelperText = (() => {
    if (isDateInPast) {
      return 'Pick today or a later date';
    }
    if (targetPeriodLoading) {
      return 'Checking which week…';
    }
    if (targetPeriod) {
      return `Comes out of the ${formatWeekRange(targetPeriod.periodStart, targetPeriod.periodEnd)} pot`;
    }
    return "Sets the week it's taken from";
  })();

  const isValid =
    (target === 'gang' ? !!selectedGangId : !!selectedWorkerId) &&
    description.trim().length >= 3 &&
    amountNum > 0 &&
    !!deductionDate &&
    !isDateInPast &&
    !potIsLocked &&
    notes.length <= NOTES_MAX_LENGTH;

  const handleSubmit = () => {
    createDeduction(
      {
        gangId: target === 'gang' ? selectedGangId : null,
        targetWorkerId: target === 'worker' ? selectedWorkerId : null,
        siteId: selectedSite?.id ?? null,
        plotId: selectedPlot?.id ?? null,
        description: description.trim(),
        amount: amountNum,
        deductionDate: deductionDate
          ? format(deductionDate, 'yyyy-MM-dd')
          : null,
        notes: notes.trim() || null,
      },
      {
        onSuccess: () => onSuccess(),
      }
    );
  };

  const gangOptions = gangs
    .slice(0, OPTION_PAGE_SIZE)
    .map(g => ({ id: g.id, label: g.name }));
  const selectedGangOption =
    gangOptions.find(g => g.id === selectedGangId) ?? null;

  const userOptions = users.map(u => ({
    id: u.id,
    label: u.fullName || u.email,
  }));
  const selectedUserOption =
    userOptions.find(u => u.id === selectedWorkerId) ?? null;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Scrollable body */}
        <Box sx={{ flex: 1, overflow: 'auto', px: 3, py: 2.5 }}>
          <Stack spacing={3} divider={<Divider flexItem />}>
            {/* What's coming off */}
            <Stack spacing={2}>
              <SectionHeading title="What's coming off" />

              <TextField
                label="Name"
                required
                value={description}
                onChange={e => setDescription(e.target.value)}
                inputProps={{ maxLength: 200 }}
                helperText={`${description.length}/200`}
              />

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Amount"
                  required
                  type="number"
                  value={amount}
                  onChange={e => setAmount(e.target.value)}
                  inputProps={{ min: 0, step: '0.01' }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">£</InputAdornment>
                    ),
                  }}
                  sx={{ flex: 1 }}
                />

                <DatePicker
                  label="Date"
                  value={deductionDate}
                  onChange={setDeductionDate}
                  minDate={today}
                  maxDate={addYears(today, 1)}
                  slotProps={{
                    textField: {
                      required: true,
                      fullWidth: true,
                      error: isDateInPast,
                      helperText: dateHelperText,
                      sx: { flex: 1 },
                    },
                  }}
                />
              </Stack>
            </Stack>

            {/* Who pays it */}
            <Stack spacing={2}>
              <SectionHeading
                title="Who pays it"
                hint="It comes out of their pot for the week you picked."
              />

              <ToggleButtonGroup
                value={target}
                exclusive
                onChange={handleTargetChange}
                fullWidth
              >
                <ToggleButton value="gang">
                  <GroupIcon sx={{ mr: 1 }} fontSize="small" />
                  Gang
                </ToggleButton>
                <ToggleButton value="worker">
                  <PersonIcon sx={{ mr: 1 }} fontSize="small" />
                  Individual worker
                </ToggleButton>
              </ToggleButtonGroup>

              {target === 'gang' && (
                <Autocomplete
                  options={gangOptions}
                  loading={gangsLoading}
                  filterOptions={x => x}
                  getOptionLabel={o => o.label}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  value={selectedGangOption}
                  onChange={(_, val) => setSelectedGangId(val?.id ?? null)}
                  onInputChange={(_, val, reason) => {
                    if (reason !== 'reset') setGangSearch(val);
                  }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label="Gang"
                      required
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {gangsLoading && (
                              <CircularProgress color="inherit" size={20} />
                            )}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              )}

              {target === 'worker' && (
                <Autocomplete
                  options={userOptions}
                  loading={usersLoading}
                  filterOptions={x => x}
                  getOptionLabel={o => o.label}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  value={selectedUserOption}
                  onChange={(_, val) => setSelectedWorkerId(val?.id ?? null)}
                  onInputChange={(_, val, reason) => {
                    if (reason !== 'reset') setWorkerSearch(val);
                  }}
                  noOptionsText={
                    debouncedWorkerSearch.length < 3
                      ? 'Type at least 3 characters to search'
                      : 'No workers found'
                  }
                  renderInput={params => (
                    <TextField
                      {...params}
                      label="Worker"
                      required
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {usersLoading && (
                              <CircularProgress color="inherit" size={20} />
                            )}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              )}

              {potIsLocked && targetPeriod && (
                <Alert severity="warning">
                  The{' '}
                  {formatWeekRange(
                    targetPeriod.periodStart,
                    targetPeriod.periodEnd
                  )}{' '}
                  pot {describeLockedPot(targetPeriod.potStatus)}, so nothing
                  more can come out of it. Pick a later date.
                </Alert>
              )}
            </Stack>

            {/* Where it came from */}
            <Stack spacing={2}>
              <SectionHeading
                title="Where it came from"
                hint="Optional. Ties the deduction to a site, and a plot if you know it."
              />

              <Autocomplete
                options={sites.slice(0, OPTION_PAGE_SIZE)}
                loading={sitesLoading}
                filterOptions={x => x}
                getOptionLabel={o => o.name}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                value={selectedSite}
                onChange={(_, val) => handleSiteChange(val)}
                onInputChange={(_, val, reason) => {
                  if (reason !== 'reset') setSiteSearch(val);
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Site"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {sitesLoading && (
                            <CircularProgress color="inherit" size={20} />
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              <Autocomplete
                options={plots}
                loading={plotsLoading}
                disabled={!selectedSite}
                filterOptions={x => x}
                getOptionLabel={o => `${o.plotNumber} — ${o.name}`}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                value={selectedPlot}
                onChange={(_, val) => setSelectedPlot(val)}
                onInputChange={(_, val, reason) => {
                  if (reason !== 'reset') setPlotSearch(val);
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Plot"
                    helperText={selectedSite ? undefined : 'Pick a site first'}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {plotsLoading && (
                            <CircularProgress color="inherit" size={20} />
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Stack>

            {/* Notes */}
            <Stack spacing={2}>
              <SectionHeading
                title="Notes"
                hint="Office only — this doesn't show in the app."
              />

              <TextField
                label="Notes"
                multiline
                minRows={3}
                value={notes}
                onChange={e => setNotes(e.target.value)}
                inputProps={{ maxLength: NOTES_MAX_LENGTH }}
                placeholder="Anything the office needs to know later"
                helperText={`${notes.length}/${NOTES_MAX_LENGTH}`}
              />
            </Stack>
          </Stack>
        </Box>

        {/* Fixed footer */}
        <Box
          sx={{
            p: 3,
            borderTop: 1,
            borderColor: 'divider',
            backgroundColor: 'background.paper',
          }}
        >
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              onClick={onCancel}
              disabled={isPending}
              fullWidth
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!isValid || isPending}
              fullWidth
              startIcon={
                isPending ? (
                  <CircularProgress size={20} color="inherit" />
                ) : undefined
              }
            >
              {isPending ? 'Creating...' : 'Create deduction'}
            </Button>
          </Stack>
        </Box>
      </Box>
    </LocalizationProvider>
  );
}
